import React, { useState, useMemo, useEffect } from 'react';
import { BookCopy, Home, Package, Landmark, ChevronLeft, ChevronRight, Search, Users, CreditCard, Receipt, Archive as ArchiveIcon, LogOut, PackageCheck } from 'lucide-react';

// Import Services & Helpers
import { apiService } from './api/apiService';
import { useDebounce } from './hooks/useDebounce';

// Import Components
import { LoginScreen } from './components/auth/LoginScreen';
import { LoadingSpinner } from './components/ui/LoadingSpinner';
import { ErrorMessage } from './components/ui/ErrorMessage';
import { ImageViewerModal } from './components/ui/ImageViewerModal';

// Import Features (Views)
import { Dashboard } from './features/Dashboard/Dashboard';
import { Orders } from './features/Orders/Orders';
import { Accounts } from './features/Accounts/Accounts';
import { Customers } from './features/Customers/Customers';
import { Accounting } from './features/Accounting/Accounting';
import { Liabilities } from './features/Liabilities/Liabilities';
import { Archive } from './features/Archive/Archive';
import { OrderProcessing } from './features/OrderProcessing/OrderProcessing';

// --- MAIN APP COMPONENT ---
function App() {
    // Authentication State
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isAuthChecked, setIsAuthChecked] = useState(false);

    // View & UI State
    const [activeView, setActiveView] = useState(() => localStorage.getItem('lastActiveView') || 'dashboard');
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewingImage, setViewingImage] = useState(null);
    const [scrollAnchor, setScrollAnchor] = useState(null);
    
    // Data State
    const [data, setData] = useState({
        orders: [],
        transactions: [],
        accounts: [],
        liabilities: [],
        archives: [],
    });
    
    // Status State
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    // --- SIDE EFFECTS ---
    useEffect(() => {
        localStorage.setItem('lastActiveView', activeView);
    }, [activeView]);

    useEffect(() => {
        const storedKey = apiService.checkAuth();
        if (storedKey) {
            setIsAuthenticated(true);
        }
        setIsAuthChecked(true);
    }, []);

    const fetchAllData = async () => {
        setError(null);
        try {
            const fetchedData = await apiService.fetchAll();
            setData(prev => ({...prev, ...fetchedData}));
        } catch (e) {
            if (e.message.includes("401")) {
                handleLogout();
                setError("Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại.");
            } else {
                setError(e.message);
            }
        } finally {
            setIsLoading(false);
        }
    };
    
    useEffect(() => {
        if (isAuthenticated) {
            setIsLoading(true);
            fetchAllData();
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (scrollAnchor) {
            const element = document.getElementById(scrollAnchor);
            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 100);
            }
            setScrollAnchor(null);
        }
    }, [data, scrollAnchor]);


    // --- HANDLERS ---
    const handleLogin = (password) => {
        if (apiService.login(password)) {
            setIsAuthenticated(true);
            return true;
        }
        return false;
    };

    const handleLogout = () => {
        apiService.logout();
        setIsAuthenticated(false);
        setData({ orders: [], transactions: [], accounts: [], liabilities: [], archives: [] });
    };

    const handleApiCall = async (callback, anchorId = null) => {
        try {
            if (anchorId) {
                setScrollAnchor(anchorId);
            }
            await callback();
            await fetchAllData();
        } catch (error) {
            setError(error.message || 'Một lỗi không mong muốn đã xảy ra.');
            setScrollAnchor(null);
        }
    };

    const apiHandlers = {
        saveOrder: (data, isEditing) => handleApiCall(() => apiService.saveOrder(data, isEditing), `order-card-${data.id}`),
        deleteOrder: (id) => handleApiCall(() => apiService.deleteOrder(id)),
        saveAccount: (data, isEditing) => handleApiCall(() => apiService.saveAccount(data, isEditing)),
        saveTransaction: (data, files, isEditing) => handleApiCall(() => apiService.saveTransaction(data, files, isEditing)),
        deleteTransaction: (id) => handleApiCall(() => apiService.deleteTransaction(id)),
        saveLiability: (data, file, isEditing) => handleApiCall(() => apiService.saveLiability(data, file, isEditing)),
        deleteLiability: (id) => handleApiCall(() => apiService.deleteLiability(id)),
        updateLiabilityStatus: (id, status) => handleApiCall(() => apiService.updateLiabilityStatus(id, status)),
        saveArchive: (file, date) => handleApiCall(() => apiService.saveArchive(file, date)),
        deleteArchive: (id) => handleApiCall(() => apiService.deleteArchive(id)),
    };

    const handleOpenFile = (url) => {
        if (!url) return;
        const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(url.split('?')[0]);
        if (isImage) {
            setViewingImage(url);
        } else {
            window.open(url, '_blank');
        }
    };
    
    const fileHandler = { uploadFile: apiService.uploadFile, open: handleOpenFile };

    const sortedData = useMemo(() => {
        const sortByDateAndId = (a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            if (dateA < dateB) return 1;
            if (dateA > dateB) return -1;
            
            if (a.id && b.id) {
                return b.id.localeCompare(a.id, undefined, { numeric: true });
            }
            return 0;
        };

        return {
            orders: [...data.orders].sort(sortByDateAndId),
            transactions: [...data.transactions].sort(sortByDateAndId),
            liabilities: [...data.liabilities].sort(sortByDateAndId),
            archives: [...data.archives].sort((a,b) => new Date(b.upload_timestamp) - new Date(a.upload_timestamp)),
            accounts: data.accounts,
        };
    }, [data]);

    const filteredOrdersGlobal = useMemo(() => {
        if (!debouncedSearchTerm) return sortedData.orders;
        const lowercasedTerm = debouncedSearchTerm.toLowerCase();
        return sortedData.orders.filter(order => 
            (order.customerName?.toLowerCase().includes(lowercasedTerm)) ||
            (order.phone?.includes(lowercasedTerm)) ||
            (order.id?.toLowerCase().includes(lowercasedTerm)) ||
            (order.items?.some(item => item.productName?.toLowerCase().includes(lowercasedTerm)))
        );
    }, [sortedData.orders, debouncedSearchTerm]);

    const renderView = () => {
        if (isLoading) return <LoadingSpinner />;
        if (error) return <ErrorMessage message={error} onRetry={fetchAllData} />;
        
        const commonProps = {
            accounts: sortedData.accounts,
            fileHandler,
        };
        
        switch (activeView) {
            case 'dashboard': return <Dashboard orders={sortedData.orders} transactions={sortedData.transactions} accounts={sortedData.accounts} />;
            case 'orderProcessing': return <OrderProcessing orders={sortedData.orders} allOrders={sortedData.orders} onSaveOrder={apiHandlers.saveOrder} />;
            case 'orders': return <Orders orders={filteredOrdersGlobal} allOrders={sortedData.orders} onSaveOrder={apiHandlers.saveOrder} onDeleteOrder={apiHandlers.deleteOrder} onUpdateOrderField={(id, field, value) => apiHandlers.saveOrder({...data.orders.find(o => o.id === id), [field]: value}, true)} />;
            case 'accounts': return <Accounts accounts={sortedData.accounts} onSaveAccount={apiHandlers.saveAccount} />;
            case 'customers': return <Customers orders={sortedData.orders} />;
            case 'accounting': return <Accounting transactions={sortedData.transactions} onSaveTransaction={apiHandlers.saveTransaction} onDeleteTransaction={apiHandlers.deleteTransaction} {...commonProps} />;
            case 'liabilities': return <Liabilities liabilities={sortedData.liabilities} onSaveLiability={apiHandlers.saveLiability} onDeleteLiability={apiHandlers.deleteLiability} onUpdateLiabilityStatus={apiHandlers.updateLiabilityStatus} {...commonProps} />;
            case 'archive': return <Archive archives={sortedData.archives} onSaveArchive={apiHandlers.saveArchive} onDeleteArchive={apiHandlers.deleteArchive} {...commonProps} />;
            default: return <div className="p-6">Chọn một mục để xem.</div>;
        }
    };
    
    const NavItem = ({ icon, label, viewName }) => (
         <button onClick={() => setActiveView(viewName)}
            className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeView === viewName ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-200'}`}>
            {icon}
            <span className={`${isSidebarOpen ? 'ml-3' : 'hidden'}`}>{label}</span>
        </button>
    );

    if (!isAuthChecked) {
        return <LoadingSpinner />;
    }

    if (!isAuthenticated) {
        return <LoginScreen onLogin={handleLogin} />;
    }
    
    return (
        <div className="flex h-screen bg-gray-100 font-sans">
            <ImageViewerModal src={viewingImage} onClose={() => setViewingImage(null)} />
            <aside className={`flex flex-col bg-white shadow-lg transition-all duration-300 ${isSidebarOpen ? 'w-64' : 'w-20'}`}>
                 <div className="flex items-center justify-center h-20 border-b">
                     <BookCopy className={`text-blue-600 transition-transform duration-500 ${isSidebarOpen ? 'transform rotate-0' : 'transform -rotate-90'}`} size={32}/>
                     {isSidebarOpen && <h1 className="text-xl font-bold ml-2 text-gray-800">Quản Lý BlogMeo</h1>}
                 </div>
                 <nav className="flex-1 px-4 py-6 space-y-2">
                    <NavItem icon={<Home size={20}/>} label="Tổng quan" viewName="dashboard" />
                    <NavItem icon={<PackageCheck size={20}/>} label="Xử lý đơn hàng" viewName="orderProcessing" />
                    <NavItem icon={<Package size={20}/>} label="Tất cả đơn hàng" viewName="orders" />
                    <NavItem icon={<Receipt size={20}/>} label="Thu Chi" viewName="accounting" />
                    <NavItem icon={<CreditCard size={20}/>} label="Công Nợ" viewName="liabilities" />
                    <NavItem icon={<ArchiveIcon size={20}/>} label="Kho Lưu Trữ" viewName="archive" />
                    <NavItem icon={<Landmark size={20}/>} label="Tài khoản" viewName="accounts" />
                    <NavItem icon={<Users size={20}/>} label="Khách hàng" viewName="customers" />
                 </nav>
            </aside>
            
            <div className="flex-1 flex flex-col">
                 <header className="flex items-center justify-between h-20 bg-white shadow-sm px-8 border-b">
                     <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="p-2 rounded-full hover:bg-gray-200">
                         {isSidebarOpen ? <ChevronLeft/> : <ChevronRight />}
                     </button>
                     <div className="relative w-full max-w-md">
                         <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20}/>
                         <input 
                            type="text"
                            placeholder="Tìm kiếm mã đơn, SĐT, tên khách..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border rounded-full bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                         />
                     </div>
                     <div className="flex items-center gap-4">
                         <button onClick={handleLogout} className="p-2 rounded-full hover:bg-red-100 text-gray-600 hover:text-red-600" title="Đăng xuất">
                           <LogOut size={20} />
                         </button>
                         <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">A</div>
                     </div>
                 </header>
                 
                 <main id="main-content" className="flex-1 p-8 overflow-y-auto">
                     {renderView()}
                 </main>
            </div>
        </div>
    );
}

export default App;
